import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { RegexEnum } from '../../enums/regex/regex.enum';

/**
 * Shared validators for common form fields (email, firstname, phone, address, etc.).
 * Used by both backoffice and fines-portal to keep validation rules consistent.
 */
@Injectable({
    providedIn: 'root',
})
export class FieldValidatorService {
    /** Required short text (e.g. firstname), max 40 chars */
    requiredShortText(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(40)];
    }

    /** Required medium text (e.g. lastname), max 100 chars */
    requiredMediumText(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(100)];
    }

    /** Required long name (e.g. company name), max 140 chars */
    requiredLongName(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(140)];
    }

    /** Optional email */
    optionalEmail(): ValidatorFn[] {
        return [Validators.email];
    }

    /** Required email */
    requiredEmail(): ValidatorFn[] {
        return [Validators.required, Validators.email];
    }

    /** Optional phone */
    optionalPhone(): ValidatorFn[] {
        return [Validators.pattern(RegexEnum.PHONE_NUMBER)];
    }

    /** Required phone */
    requiredPhone(): ValidatorFn[] {
        return [
            Validators.required,
            Validators.pattern(RegexEnum.PHONE_NUMBER),
        ];
    }

    /** National number (e.g. Belgian RRN), 11 digits */
    nationalNumber(): ValidatorFn[] {
        return [
            Validators.minLength(11),
            Validators.maxLength(11),
            Validators.pattern(RegexEnum.DIGITS_ONLY),
        ];
    }

    /** Company number (Belgian format) */
    companyNumber(): ValidatorFn[] {
        return [Validators.pattern(RegexEnum.COMPANY)];
    }

    /** Optional box number, max 6 chars */
    optionalBoxNumber(): ValidatorFn[] {
        return [Validators.maxLength(6)];
    }

    /** Optional house number */
    optionalHouseNumber(): ValidatorFn[] {
        return [
            Validators.maxLength(20),
            Validators.pattern(RegexEnum.HOUSE_NUMBER),
        ];
    }

    /** Required street, max 200 chars */
    requiredStreet(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(200)];
    }

    /** Required house number */
    requiredHouseNumber(): ValidatorFn[] {
        return [
            Validators.required,
            Validators.maxLength(20),
            Validators.pattern(RegexEnum.HOUSE_NUMBER),
        ];
    }

    /** Required postal code */
    requiredPostalCode(): ValidatorFn[] {
        return [
            Validators.required,
            Validators.maxLength(20),
            Validators.pattern(RegexEnum.POSTAL_CODE),
        ];
    }

    /** Required city, max 100 chars */
    requiredCity(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(100)];
    }
}
